import React, { Fragment, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { get, map } from 'lodash';
import { Crumb, JsonLdScript } from 'components/atoms';
import isBrowser from 'utils/isBrowser';

import './BreadCrumb.scss';

function ensureTrailingSlash(url) {
	if (!url) return url;
	return url.endsWith('/') ? url : `${url}/`;
}

function isAbsoluteUrl(raw) {
	return /^https?:\/\//i.test(raw);
}

function buildHref(rawHref, rootUrl) {
	if (!rawHref) return rootUrl;
	if (isAbsoluteUrl(rawHref)) return rawHref;
	const path = rawHref.startsWith('/') ? rawHref : `/${rawHref}`;
	return `${rootUrl.replace(/\/$/, '')}${path}`;
}

function joinUrl(base, path) {
	if (!base) return path || '';
	if (!path) return base;
	return `${base.replace(/\/+$/, '')}/${path.replace(/^\/+/, '')}`;
}

function getCrumbHref(crumb) {
	const raw = (
		get(crumb, 'props.href') ||
		get(crumb, 'props.to') ||
		get(crumb, 'props.id') ||
		''
	).toString();
	return raw.split('?').shift();
}

function createCLPBreadcrumbSchema(
	baseUrl = 'https://www.selfstorage.com',
	crumbs = [],
) {
	const ROOT_URL = baseUrl;

	const filtered = crumbs.filter((crumb) => crumb?.props?.label);

	const itemListElement = filtered.map((crumb, index) => {
		const rawHref = (get(crumb, 'props.href', '') || '').split('?').shift();
		const absoluteUrl = buildHref(rawHref, ROOT_URL);
		const idUrl = ensureTrailingSlash(absoluteUrl);

		return {
			'@type': 'ListItem',
			position: index + 1,
			item: {
				'@id': idUrl,
				name: crumb.props.name || crumb.props.label,
				url: idUrl,
			},
		};
	});

	const cityCrumb = filtered.find(
		(c) => c?.props?.className === 'sublocality',
	);
	let landingUrl = '';

	if (cityCrumb) {
		const raw = (get(cityCrumb, 'props.href', '') || '').split('?').shift();
		landingUrl = ensureTrailingSlash(buildHref(raw, ROOT_URL));
	} else {
		landingUrl =
			get(
				itemListElement,
				`[${itemListElement.length - 1}].item['@id']`,
				'',
			) || ensureTrailingSlash(ROOT_URL);
	}

	const breadcrumbListId = `${landingUrl.replace(/#.*$/, '')}#breadcrumb`;
	return {
		'@type': 'BreadcrumbList',
		'@id': breadcrumbListId,
		itemListElement,
	};
}

function createCrumbSchema(crumbs) {
	return {
		'@context': 'http://schema.org',
		'@type': 'BreadcrumbList',
		itemListElement: crumbs
			.filter((crumb) => crumb.props.label)
			.map((crumb, index) => ({
				'@type': 'ListItem',
				position: index + 1,
				item: {
					'@id': get(crumb, 'props.id', get(crumb, 'props.to', ''))
						.split('?')
						.shift(),
					name: crumb.props.name || crumb.props.label,
				},
			})),
	};
}

/** ---------- Component ---------- */
export function Breadcrumbs({
	location = {},
	crumbs: crumbsProp = [],
	cityUrl,
	stateUrl,
	baseUrl,
	tertiary,
	isCLPPage,
	className,
	pspSgpBreadCrumbs,
}) {
	const { fullState, subLocality, city } = location || {};
	const sub = subLocality || city;

	// When pspSgpBreadCrumbs is provided, build crumbs directly from those items
	// and skip the default location-based crumb generation (restored from master's enhance HOC)
	const builtFromPspSgp = useMemo(() => {
		if (!pspSgpBreadCrumbs?.length) return null;
		return pspSgpBreadCrumbs.map((item, index) => (
			<Crumb
				key={item.key || `static-crumb-${index}`}
				href={item.href || ''}
				id={item.href || ''}
				label={item.label}
				name={item.label}
				isLast={index === pspSgpBreadCrumbs.length - 1}
				className={
					index === pspSgpBreadCrumbs.length - 1
						? undefined
						: 'custom'
				}
			/>
		));
	}, [pspSgpBreadCrumbs]);

	const newCrumbs = useMemo(() => {
		if (builtFromPspSgp) return builtFromPspSgp;

		const crumbs = [];

		crumbs.push(
			<Crumb
				href={`${baseUrl}`}
				id={`${baseUrl}`}
				key="crumb-home"
				label="Home"
				name="Home"
				className="state custom"
				baseUrl={baseUrl}
				tertiary={tertiary}
			/>,
			<Crumb
				href={joinUrl(baseUrl, '/storage-units-near-me')}
				id={joinUrl(baseUrl, '/storage-units-near-me')}
				key="crumb-find-storage"
				label="Find storage"
				name="Find storage"
				className="custom"
			/>,
		);

		if (stateUrl) {
			crumbs.push(
				<Crumb
					key={`crumb-state-${(fullState || stateUrl || '').toString()}`}
					id={joinUrl(baseUrl, stateUrl)}
					href={joinUrl(baseUrl, stateUrl)}
					isLast={!cityUrl && !crumbsProp.length}
					label={fullState}
					name={fullState}
					className="state custom"
					tertiary={tertiary}
				/>,
			);
		}

		if (cityUrl) {
			const isLast = !crumbsProp.length;
			const cityName = ((sub ?? cityUrl) || '').toString().trim();
			const hasLocation = fullState && cityName;
			const label = cityName
				? `${cityName} Self Storage`
				: 'Self Storage';

			crumbs.push(
				<Crumb
					key={`crumb-city-${cityName}`}
					id={joinUrl(baseUrl, cityUrl)}
					className="sublocality custom"
					href={joinUrl(baseUrl, cityUrl)}
					isLast={isLast}
					label={label}
					name={
						hasLocation
							? `Self storage in ${fullState}, ${cityName}`
							: 'Self Storage'
					}
				>
					{label}
				</Crumb>,
			);
		}

		return [...crumbs, ...crumbsProp];
	}, [
		builtFromPspSgp,
		baseUrl,
		tertiary,
		stateUrl,
		cityUrl,
		crumbsProp,
		fullState,
		sub,
	]);

	const fullCrumbsSchema = useMemo(() => {
		if (builtFromPspSgp) return newCrumbs;
		if (crumbsProp.at(-1)?.key === 'facility') {
			return newCrumbs.filter((_, index) => [0, 3, 4].includes(index));
		}
		return newCrumbs;
	}, [builtFromPspSgp, newCrumbs, crumbsProp]);

	// PSP/SGP pages handle their own schema via buildSchema.js — skip JSON-LD here
	const crumbJSON = useMemo(() => {
		if (builtFromPspSgp) return null;
		return isCLPPage
			? createCLPBreadcrumbSchema(baseUrl, newCrumbs)
			: createCrumbSchema(fullCrumbsSchema);
	}, [builtFromPspSgp, isCLPPage, baseUrl, newCrumbs, fullCrumbsSchema]);

	const backHref = useMemo(() => {
		const items = Array.isArray(newCrumbs) ? newCrumbs : [];
		if (items.length >= 2) {
			const prev = items.at(-2);
			const href = getCrumbHref(prev);
			if (href) return href;
		}
		const firstWithHref = items.find((c) => !!getCrumbHref(c));
		if (firstWithHref) return getCrumbHref(firstWithHref);
		return baseUrl || '/';
	}, [newCrumbs, baseUrl]);

	const onMobileBackClick = useCallback((e) => {
		if (isBrowser() && globalThis.history?.length > 1) {
			e.preventDefault();
			globalThis.history.back();
		}
	}, []);

	// PSP/SGP pages always want the full descriptive trail (no strong "back"
	// semantic on those pages), regardless of viewport - never mount the
	// back control there. Everywhere else, both the full list and the back
	// control stay mounted (so crawlers and SSR always get the real crumb
	// links + JSON-LD); each consumer's own SCSS decides whether/where to
	// hide the full list on mobile (see FacilityPage.scss, UnitPage.scss,
	// OGFFacilityPage.scss) - this component doesn't impose a global
	// mobile-hide breakpoint on the list itself.
	const showMobileBack = !builtFromPspSgp;

	return (
		<Fragment>
			<ul
				className={classnames('ss-breadcrumbs', className)}
				id="breadcrumb"
			>
				{map(newCrumbs, (crumb) => (
					<li
						className="ss-breadcrumb"
						key={crumb.key}
					>
						{crumb}
					</li>
				))}
			</ul>

			{showMobileBack && (
				<a
					href={backHref}
					className="ss-breadcrumbs__mobile-back"
					onClick={onMobileBackClick}
					aria-label="Go back"
				>
					<span
						aria-hidden="true"
						className="ss-breadcrumbs__chevron"
					/>
					<span className="ss-breadcrumbs__back-text">Back</span>
				</a>
			)}

			{crumbJSON && <JsonLdScript schema={crumbJSON} />}
		</Fragment>
	);
}

Breadcrumbs.propTypes = {
	className: PropTypes.string,
	location: PropTypes.object,
	crumbs: PropTypes.arrayOf(PropTypes.node),
	cityUrl: PropTypes.string,
	stateUrl: PropTypes.string,
	baseUrl: PropTypes.string,
	tertiary: PropTypes.bool,
	isCLPPage: PropTypes.bool,
	pspSgpBreadCrumbs: PropTypes.arrayOf(
		PropTypes.shape({
			key: PropTypes.string.isRequired,
			label: PropTypes.string.isRequired,
			href: PropTypes.string,
		}),
	),
};

export default Breadcrumbs;
